'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { useTable, flexRender } from '@tanstack/react-table';
import type { ColumnDef } from '@/lib/table-features';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataTablePagination } from '@/components/ui/table/data-table-pagination';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import { appTableFeatures } from '@/lib/table-features';
import { repairSettingsQueries } from '../services/repair-settings.queries';
import {
  useDeleteRepairReasonMutation,
  useStoreRepairReasonMutation,
  useUpdateRepairReasonMutation
} from '../services/repair-settings.mutations';
import { REPAIR_SETTINGS_HASH } from '../constants';
import type { RepairDestination, RepairReason, LabType } from '../types';

interface RepairDestinationSectionProps {
  dest: RepairDestination;
  nameKey: 'name_en' | 'name_ar';
  onAddReason: (dest: RepairDestination) => void;
}

function RepairDestinationSection({
  dest,
  nameKey,
  onAddReason
}: RepairDestinationSectionProps) {
  const t = useTranslations('repairSettings');
  const [view, setView] = useDataView(`repair-reasons-${dest.id}`);
  // Overlay open-state lives in the URL hash so overlays are deep-linkable,
  // survive refresh, and close on browser Back. Reason ids are unique across
  // destinations, so edit/delete don't need the destination id as a param.
  const {
    open: deleteReasonOpen,
    param: deleteReasonId,
    openWith: openDeleteReason,
    onOpenChange: closeDeleteReason
  } = useHashParamDialog(REPAIR_SETTINGS_HASH.deleteReason);
  const {
    open: editReasonOpen,
    param: editReasonId,
    openWith: openEditReasonHash,
    onOpenChange: closeEditReason
  } = useHashParamDialog(REPAIR_SETTINGS_HASH.editReason);
  const [editNameEn, setEditNameEn] = useState('');
  const [editNameAr, setEditNameAr] = useState('');
  const {
    open: bulkDeleteOpen,
    param: bulkDeleteDestId,
    openWith: openBulkDelete,
    onOpenChange: closeBulkDelete
  } = useHashParamDialog(REPAIR_SETTINGS_HASH.bulkDeleteReasons);
  const bulkOpen = bulkDeleteOpen && bulkDeleteDestId === String(dest.id);
  const deleteReason = useDeleteRepairReasonMutation();
  const updateReason = useUpdateRepairReasonMutation();

  const openEditReason = (reason: RepairReason) => {
    setEditNameEn(reason.name_en);
    setEditNameAr(reason.name_ar);
    openEditReasonHash(String(reason.id));
  };

  async function confirmEditReason() {
    if (!editTarget || !editNameEn.trim() || !editNameAr.trim()) return;
    try {
      await updateReason.mutateAsync({
        id: editTarget.id,
        data: { name_en: editNameEn, name_ar: editNameAr }
      });
      toast.success(t('reasonUpdated'));
      closeEditReason(false);
    } catch {
      /* error toast is handled by the shared MutationCache */
    }
  }

  const prefix = `r${dest.id}`;
  const [page, setPage] = useQueryState(
    `${prefix}_page`,
    parseAsInteger.withDefault(1)
  );
  const [perPage, setPerPage] = useQueryState(
    `${prefix}_per_page`,
    parseAsInteger.withDefault(10)
  );
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);

  const sortParam = sorting[0];
  const reasonsQueryParams = useMemo(
    () => ({
      page,
      per_page: perPage,
      sort_by: sortParam?.id as 'name_en' | 'name_ar' | undefined,
      sort_direction: sortParam
        ? ((sortParam.desc ? 'desc' : 'asc') as 'asc' | 'desc')
        : undefined
    }),
    [page, perPage, sortParam]
  );

  const { data: reasonsData, isPlaceholderData: reasonsPlaceholder } = useQuery(
    repairSettingsQueries.reasonsList(dest.id, reasonsQueryParams)
  );

  const reasons = useMemo(() => reasonsData?.data ?? [], [reasonsData]);
  const reasonsPagination = reasonsData?.pagination;

  // Resolve #delete-reason/<id> and #edit-reason/<id> back to the loaded row
  // (must be on the current page; deep-linking an off-page id simply won't
  // open the dialog).
  const deleteTarget = deleteReasonOpen
    ? (reasons.find((r) => String(r.id) === deleteReasonId) ?? null)
    : null;
  const editTarget = editReasonOpen
    ? (reasons.find((r) => String(r.id) === editReasonId) ?? null)
    : null;

  useEffect(() => {
    if (!reasonsPagination) return;
    if (
      reasonsPagination.totalPages > 0 &&
      page > reasonsPagination.totalPages
    ) {
      void setPage(reasonsPagination.totalPages);
    } else if (reasonsPagination.totalPages === 0 && page !== 1) {
      void setPage(1);
    }
  }, [reasonsPagination, page, setPage]);

  const columns: ColumnDef<RepairReason>[] = useMemo(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(!!v)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40
      },
      {
        id: 'name_en',
        accessorKey: 'name_en',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('nameEn')} />
        ),
        cell: ({ row }) => (
          <span className='text-sm'>{row.original.name_en}</span>
        )
      },
      {
        id: 'name_ar',
        accessorKey: 'name_ar',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('nameAr')} />
        ),
        cell: ({ row }) => (
          <span className='text-sm' dir='rtl'>
            {row.original.name_ar}
          </span>
        )
      },
      {
        id: 'actions',
        header: () => <div className='text-end'>{t('actions')}</div>,
        cell: ({ row }) => (
          <div className='flex justify-end gap-1'>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={() => {
                setEditNameEn(row.original.name_en);
                setEditNameAr(row.original.name_ar);
                openEditReasonHash(String(row.original.id));
              }}
            >
              <Pencil className='h-3.5 w-3.5' />
            </Button>
            <Button
              variant='ghost'
              size='icon'
              className='text-destructive h-7 w-7'
              onClick={() => openDeleteReason(String(row.original.id))}
            >
              <Trash2 className='h-3.5 w-3.5' />
            </Button>
          </div>
        ),
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, openEditReasonHash, openDeleteReason]
  );

  const table = useTable({
    features: appTableFeatures,
    data: reasons,
    columns,
    manualPagination: true,
    manualSorting: true,
    pageCount: reasonsPagination?.totalPages ?? 0,
    state: {
      pagination: {
        pageIndex: Math.max(0, (page ?? 1) - 1),
        pageSize: perPage ?? 10
      },
      sorting
    },
    onPaginationChange: (updater) => {
      const prev = {
        pageIndex: Math.max(0, (page ?? 1) - 1),
        pageSize: perPage ?? 10
      };
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const sizeChanged = next.pageSize !== prev.pageSize;
      const nextPageIndex = sizeChanged ? 0 : next.pageIndex;
      void setPage(nextPageIndex + 1 === 1 ? null : nextPageIndex + 1);
      void setPerPage(next.pageSize === 10 ? null : next.pageSize);
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(next);
      void setPage(null);
    },
    autoResetPageIndex: false
  });

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('nameEn'), value: 'name_en' },
      { label: t('nameAr'), value: 'name_ar' }
    ],
    [t]
  );

  async function confirmDeleteSingle() {
    if (!deleteTarget) return;
    try {
      await deleteReason.mutateAsync(deleteTarget.id);
      toast.success(t('reasonDeleted'));
    } catch {
      /* error toast is handled by the shared MutationCache */
    }
    closeDeleteReason(false);
  }

  async function confirmBulkDelete() {
    try {
      for (const row of selectedRows) {
        await deleteReason.mutateAsync(row.original.id);
      }
      toast.success(t('reasonDeleted'));
    } catch {
      /* error toast is handled by the shared MutationCache */
    }
    table.resetRowSelection();
    closeBulkDelete(false);
  }

  return (
    <Card>
      <CardHeader className='pb-3'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <div className='flex min-w-0 items-center gap-2'>
            <CardTitle className='truncate text-sm font-semibold'>
              {dest[nameKey]}
            </CardTitle>
            <Badge
              variant={dest.is_active ? 'default' : 'secondary'}
              className='shrink-0 text-xs'
            >
              {dest.is_active ? t('active') : t('inactive')}
            </Badge>
            {dest.supervisor && (
              <span className='text-muted-foreground hidden truncate text-xs sm:inline'>
                · {dest.supervisor.name}
              </span>
            )}
            <Badge variant='outline' className='shrink-0 text-xs tabular-nums'>
              {reasonsPagination?.total ?? 0}
            </Badge>
          </div>

          <div className='flex shrink-0 items-center gap-1.5'>
            {view !== 'table' && (
              <SortControl
                options={sortOptions}
                sorting={tableState.sorting}
                onSortingChange={(s) => table.setSorting(s)}
              />
            )}
            <DataViewToggle
              value={view}
              onValueChange={setView}
              pageKey={`repair-reasons-${dest.id}`}
            />
            <Button
              variant='outline'
              size='sm'
              className='h-7 text-xs'
              onClick={() => onAddReason(dest)}
            >
              <Plus className='me-1 h-3.5 w-3.5' />
              {t('addReason')}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className='pt-0'>
        {view === 'table' ? (
          <div className='space-y-3'>
            {selectedRows.length > 0 && (
              <div className='bg-muted/50 flex items-center gap-3 rounded-md px-3 py-2'>
                <span className='text-muted-foreground text-sm'>
                  {selectedRows.length} {t('selected')}
                </span>
                <Button
                  variant='destructive'
                  size='sm'
                  className='h-7'
                  onClick={() => openBulkDelete(String(dest.id))}
                >
                  <Trash2 className='me-1.5 h-3.5 w-3.5' />
                  {t('delete')} ({selectedRows.length})
                </Button>
              </div>
            )}

            <div
              className={cn(
                'rounded-md border',
                reasonsPlaceholder && 'opacity-60 transition-opacity'
              )}
            >
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((hg) => (
                    <TableRow key={hg.id}>
                      {hg.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          style={{
                            width:
                              header.getSize() !== 150
                                ? header.getSize()
                                : undefined
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className='text-muted-foreground h-16 text-center text-sm'
                      >
                        {t('noReasons')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <DataTablePagination table={table} pageSizeOptions={[5, 10, 20]} />
          </div>
        ) : view === 'grid' ? (
          <div className='space-y-4'>
            {reasons.length === 0 ? (
              <p className='text-muted-foreground py-4 text-center text-sm'>
                {t('noReasons')}
              </p>
            ) : (
              <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                {reasons.map((r) => (
                  <div
                    key={r.id}
                    className='hover:bg-muted/30 group rounded-md border p-3 transition-colors'
                  >
                    <div className='flex items-start justify-between gap-2'>
                      <div className='min-w-0 space-y-0.5'>
                        <p className='truncate text-sm font-medium'>
                          {r.name_en}
                        </p>
                        <p
                          className='text-muted-foreground truncate text-xs'
                          dir='rtl'
                        >
                          {r.name_ar}
                        </p>
                      </div>
                      <div className='flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100'>
                        <Button
                          variant='ghost'
                          size='icon'
                          className='h-6 w-6'
                          onClick={() => openEditReason(r)}
                        >
                          <Pencil className='h-3 w-3' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon'
                          className='text-destructive h-6 w-6'
                          onClick={() => openDeleteReason(String(r.id))}
                        >
                          <Trash2 className='h-3 w-3' />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <PaginationControls
              page={page ?? 1}
              pageCount={reasonsPagination?.totalPages ?? 1}
              perPage={perPage ?? 10}
              total={reasonsPagination?.total ?? 0}
              onPageChange={(p) => void setPage(p === 1 ? null : p)}
              onPerPageChange={(s) => {
                void setPerPage(s === 10 ? null : s);
                void setPage(null);
              }}
              pageSizeOptions={[5, 10, 20]}
            />
          </div>
        ) : (
          <div className='space-y-4'>
            {reasons.length === 0 ? (
              <p className='text-muted-foreground py-4 text-center text-sm'>
                {t('noReasons')}
              </p>
            ) : (
              <div className='space-y-1'>
                {reasons.map((r) => (
                  <div
                    key={r.id}
                    className='hover:bg-muted/50 group flex items-center justify-between rounded-md px-2 py-1.5 transition-colors'
                  >
                    <span className='text-sm'>{r[nameKey]}</span>
                    <div className='flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100'>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='h-6 w-6'
                        onClick={() => openEditReason(r)}
                      >
                        <Pencil className='h-3 w-3' />
                      </Button>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='text-destructive h-6 w-6'
                        onClick={() => openDeleteReason(String(r.id))}
                      >
                        <Trash2 className='h-3 w-3' />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <PaginationControls
              page={page ?? 1}
              pageCount={reasonsPagination?.totalPages ?? 1}
              perPage={perPage ?? 10}
              total={reasonsPagination?.total ?? 0}
              onPageChange={(p) => void setPage(p === 1 ? null : p)}
              onPerPageChange={(s) => {
                void setPerPage(s === 10 ? null : s);
                void setPage(null);
              }}
              pageSizeOptions={[5, 10, 20]}
            />
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!deleteTarget} onOpenChange={closeDeleteReason}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirmDescription', {
                name: deleteTarget?.[nameKey] ?? ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteSingle}>
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!bulkOpen} onOpenChange={closeBulkDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bulkDeleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkDeleteConfirmDescription', {
                count: selectedRows.length
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulkDelete}>
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editTarget} onOpenChange={closeEditReason}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('editReason')}</DialogTitle>
          </DialogHeader>
          <div className='space-y-4 py-2'>
            <div className='space-y-1.5'>
              <Label>{t('nameEn')}</Label>
              <Input
                value={editNameEn}
                onChange={(e) => setEditNameEn(e.target.value)}
              />
            </div>
            <div className='space-y-1.5'>
              <Label>{t('nameAr')}</Label>
              <Input
                value={editNameAr}
                onChange={(e) => setEditNameAr(e.target.value)}
                dir='rtl'
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={confirmEditReason}
              disabled={
                !editNameEn.trim() ||
                !editNameAr.trim() ||
                updateReason.isPending
              }
            >
              {updateReason.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

interface RepairLabSectionProps {
  destinations: RepairDestination[];
  nameKey: 'name_en' | 'name_ar';
  onAddReason: (dest: RepairDestination) => void;
}

function RepairLabSection({
  destinations,
  nameKey,
  onAddReason
}: RepairLabSectionProps) {
  const t = useTranslations('repairSettings');

  if (destinations.length === 0) {
    return (
      <Card className='border-dashed'>
        <CardContent className='flex items-center justify-center py-10'>
          <p className='text-muted-foreground text-sm'>{t('noDestinations')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-3'>
      {destinations.map((dest) => (
        <RepairDestinationSection
          key={dest.id}
          dest={dest}
          nameKey={nameKey}
          onAddReason={onAddReason}
        />
      ))}
    </div>
  );
}

export function RepairSettingsPage() {
  const t = useTranslations('repairSettings');
  const locale = useLocale();
  const nameKey: 'name_en' | 'name_ar' =
    locale === 'ar' ? 'name_ar' : 'name_en';

  const [tab, setTab] = useQueryState(
    'tab',
    parseAsString.withDefault('internal_client')
  );

  const { data, isPending } = useQuery(repairSettingsQueries.public());
  const allDestinations = useMemo(() => data?.data ?? [], [data?.data]);

  const byLab = useMemo(() => {
    const groups: Record<LabType, RepairDestination[]> = {
      internal_client: [],
      internal_oman: []
    };
    for (const dest of allDestinations) {
      groups[dest.lab_type]?.push(dest);
    }
    return groups;
  }, [allDestinations]);

  const storeReason = useStoreRepairReasonMutation();

  // Add-reason dialog open-state lives in the URL hash (#add-reason/<destId>)
  // so it is deep-linkable, survives refresh, and closes on browser Back.
  const {
    open: addReasonOpen,
    param: addReasonDestId,
    openWith: openAddReasonHash,
    onOpenChange: closeAddReason
  } = useHashParamDialog(REPAIR_SETTINGS_HASH.addReason);
  const addReasonDest = addReasonOpen
    ? (allDestinations.find((d) => String(d.id) === addReasonDestId) ?? null)
    : null;

  const [reasonNameEn, setReasonNameEn] = useState('');
  const [reasonNameAr, setReasonNameAr] = useState('');

  const openAddReason = (dest: RepairDestination) => {
    setReasonNameEn('');
    setReasonNameAr('');
    openAddReasonHash(String(dest.id));
  };

  const handleSaveReason = () => {
    if (!reasonNameEn.trim() || !reasonNameAr.trim() || !addReasonDest) return;
    storeReason.mutate(
      {
        destId: addReasonDest.id,
        data: { name_en: reasonNameEn, name_ar: reasonNameAr }
      },
      {
        onSuccess: () => {
          toast.success(t('reasonCreated'));
          closeAddReason(false);
        }
      }
    );
  };

  if (isPending) {
    return (
      <div className='space-y-6'>
        <div>
          <Skeleton className='h-6 w-40' />
          <Skeleton className='mt-2 h-4 w-80' />
        </div>
        <Skeleton className='h-9 w-64' />
        <div className='space-y-3'>
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardHeader className='pb-3'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <Skeleton className='h-5 w-36' />
                    <Skeleton className='h-5 w-14 rounded-full' />
                  </div>
                  <div className='flex gap-2'>
                    <Skeleton className='h-7 w-20 rounded-md' />
                    <Skeleton className='h-7 w-7 rounded-md' />
                  </div>
                </div>
              </CardHeader>
              <CardContent className='pt-0'>
                <Skeleton className='h-28 w-full rounded-md' />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-xl font-semibold'>{t('pageTitle')}</h2>
        <p className='text-muted-foreground mt-0.5 text-sm'>
          {t('pageSubtitle')}
        </p>
      </div>

      <Tabs
        value={tab ?? 'internal_client'}
        onValueChange={(v) => void setTab(v)}
      >
        <TabsList className='mb-4'>
          <TabsTrigger value='internal_client'>
            {t('internalClient')}
          </TabsTrigger>
          <TabsTrigger value='internal_oman'>{t('internalOman')}</TabsTrigger>
        </TabsList>

        <TabsContent value='internal_client' className='mt-0'>
          <RepairLabSection
            destinations={byLab.internal_client}
            nameKey={nameKey}
            onAddReason={openAddReason}
          />
        </TabsContent>

        <TabsContent value='internal_oman' className='mt-0'>
          <RepairLabSection
            destinations={byLab.internal_oman}
            nameKey={nameKey}
            onAddReason={openAddReason}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={addReasonOpen} onOpenChange={closeAddReason}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('addReason')}
              {addReasonDest && (
                <span className='text-muted-foreground ms-1 text-sm font-normal'>
                  — {addReasonDest[nameKey]}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className='space-y-4 py-2'>
            <div className='space-y-1.5'>
              <Label>{t('nameEn')}</Label>
              <Input
                value={reasonNameEn}
                onChange={(e) => setReasonNameEn(e.target.value)}
              />
            </div>
            <div className='space-y-1.5'>
              <Label>{t('nameAr')}</Label>
              <Input
                value={reasonNameAr}
                onChange={(e) => setReasonNameAr(e.target.value)}
                dir='rtl'
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleSaveReason}
              disabled={
                !reasonNameEn.trim() ||
                !reasonNameAr.trim() ||
                storeReason.isPending
              }
            >
              {storeReason.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
